import { gemResult } from '../storyFixtures.js';
import type { WorkflowRun } from '../workflows.types.js';
import { bannerResultSchema, bannerStoryboardResultSchema } from './banner/bannerResult.js';
import { ideationBriefSchema } from './brief/briefResult.js';
import { type StoryboardVersion, storyboardResultSchema } from './storyboard/storyboardResult.js';
import { videoResultSchema } from './video/videoResult.js';

export const ideationBrief = {
  name: 'Everyday proof, unexpected results',
  body: 'Open with a real morning routine, show the product solving one familiar frustration, then close on a clear offer. Keep the demonstration tactile and the language conversational.',
  kpi: 'CTR',
  creative_type: 'Video',
  campaign_objectives: ['Conversions'],
  concepts: ['Product demonstration'],
  formats: ['9:16'],
  durations: ['15 seconds'],
  categories: [
    {
      name: 'Visual Elements',
      elements: [
        {
          feature: 'Hook Scene Layout Type',
          tag: 'Split screen',
          source: 'gems',
          source_label: 'Hidden gem',
          recommended: true,
          figures: [{ network: 'meta', kpi: 0.041, lift: 1.34, rank: 8.7, spend: 1800 }],
        },
      ],
    },
    {
      name: 'Messaging',
      elements: [
        {
          feature: 'Hook Message',
          tag: 'Audience callout',
          source: 'scan',
          source_label: 'Proven element',
          figures: [{ network: 'meta', kpi: 0.037, lift: 1.21, rank: 7.9, spend: 2100 }],
        },
      ],
    },
    { name: 'Audio', elements: [] },
  ],
  empty_categories: ['Audio'],
};
export const ideationBriefResult = {
  result_type: 'ideation_brief',
  summary: '2 elements across 2 of 3 categories',
  briefs: [ideationBrief],
};
export const hiddenGemsResult = {
  result_type: 'hidden_gems',
  summary: '2 hidden gems beating the account average on CTR',
  gems: ['Split screen', 'Product close-up'].map((tag) => ({
    ...(gemResult.gems[0] as NonNullable<(typeof gemResult.gems)[number]>),
    tag,
    category_gem_count: 2,
  })),
};
export const storyboardResult = {
  result_type: 'storyboard',
  summary: 'Created three storyboard versions.',
  storyboard: {
    name: 'Everyday proof — three ways in',
    body: 'Three executions of one brief. Lead with the familiar problem, make the benefit visible, and leave enough time to read the offer.',
    format: 'Video',
    duration_seconds: 15,
    aspect_ratio: '9:16',
    primary_kpi: 'CTR',
    source_brief: ideationBrief,
    context: 'Everyday essentials for busy people. Warm, useful, and direct.',
    production_instructions:
      'Show the product in natural light. Keep the end card visible for three seconds.',
    elements: {},
    references: [
      {
        id: 'performer',
        strategy: 'top_performer',
        creative: { creative_id: 'META_1042' },
        selection_reason: 'Strongest CTR among comparable product demonstrations.',
        observed: true,
        structure: 'Problem → product demonstration → offer.',
      },
    ],
    warnings: [],
  },
  versions: [
    {
      name: 'The morning shortcut',
      strategy: 'top_performer',
      strategy_label: 'Top-performer inspired',
    },
    {
      name: 'One product, three moments',
      strategy: 'top_spender',
      strategy_label: 'Top-spender based',
    },
    { name: 'Put it to the test', strategy: 'new_concept', strategy_label: 'Net-new concept' },
  ].map((version) => ({
    ...version,
    display_chips: ['Product-led', 'Natural light', 'Conversational'],
    rationale: 'Open with a familiar frustration, then demonstrate the product benefit.',
    creative_premise: 'A small change makes the everyday routine feel easier.',
    departure_from_references: 'Replace a polished studio setup with a lived-in morning routine.',
    reference_ids: ['performer'],
    cta: 'Find your everyday essential',
    creative_direction: {
      music: 'Light rhythmic percussion, ducked under voiceover.',
      hero_product: 'Everyday essentials kit',
      tone: 'Warm and confident',
      visual_style: ['Natural light', 'Tactile close-ups'],
      palette: ['#155DFC', '#FFFFFF'],
      element_ids: [],
    },
    scenes: [
      storyboardScene({
        shot_label: 'The familiar frustration',
        camera_style: 'Handheld',
        objective: 'Hook',
        end_seconds: 4,
        visual: 'A cluttered morning routine introduces the everyday frustration.',
        on_screen_text: 'Mornings can be easier.',
        voiceover: 'A little less effort. A lot more ease.',
        audio: 'Soft room tone under the music.',
      }),
      storyboardScene({
        shot_label: 'Show the difference',
        framing: 'Medium',
        objective: 'Proof',
        start_seconds: 4,
        end_seconds: 12,
        visual: 'Hands demonstrate the product in a sunlit kitchen. Hold on the practical benefit.',
        on_screen_text: 'One simple change.',
        voiceover: 'Made for the way you actually live.',
        audio: 'Natural product sounds.',
      }),
      storyboardScene({
        shot_label: 'Make it yours',
        objective: 'CTA',
        start_seconds: 12,
        end_seconds: 15,
        visual: 'Clean product hero and a readable offer card.',
        on_screen_text: 'Find your everyday essential',
        audio: 'Music resolves.',
        cta: 'Shop the collection',
      }),
    ],
  })),
};
export const videoResult = {
  result_type: 'video',
  summary: 'Rendered 1 of 2 selected versions.',
  videos: [
    {
      version_name: 'The morning shortcut',
      strategy: 'top_performer',
      status: 'done',
      video_url: 'gs://example-output/render.mp4',
      // Served from libs/ui/.storybook/public; the real service returns signed https URLs.
      playback_url: new URL('/storybook-preview.mp4', location.origin).href,
      duration_seconds: 15,
      error: null,
      warnings: [],
      version: storyboardResult.versions[0],
    },
    {
      version_name: 'Put it to the test',
      strategy: 'new_concept',
      status: 'failed',
      video_url: null,
      duration_seconds: null,
      error: 'The rendering provider timed out. The other version is available.',
      warnings: [],
      version: storyboardResult.versions[2],
    },
  ],
};
export const bannerStoryboardResult = {
  result_type: 'banner_storyboard',
  summary: 'Created three banner versions.',
  storyboard: {
    name: 'Everyday proof — three banners',
    body: 'Three static executions of one brief.',
    aspect_ratio: '1:1',
    primary_kpi: 'CTR',
    context: storyboardResult.storyboard.context,
    production_instructions: '',
    source_brief: { ...ideationBrief, creative_type: 'Banner' },
    elements: {},
    references: storyboardResult.storyboard.references,
    warnings: [],
  },
  versions: storyboardResult.versions.map(
    ({ display_chips: _chips, scenes: _scenes, cta: _cta, ...version }, index) => ({
      ...version,
      banner: {
        headline: ['Mornings, made easier', 'One kit, every moment', 'Put it to the test'][index],
        subline: 'Everyday essentials that just work.',
        cta: 'Shop now',
        visual: 'The product on a sunlit kitchen counter.',
        layout: 'Headline top left, product right, CTA bottom.',
        element_ids: [],
      },
    }),
  ),
};
const [firstBannerVersion, , thirdBannerVersion] = bannerStoryboardResult.versions;
export const bannerResult = {
  result_type: 'banner',
  summary: 'Rendered 3 of 4 banners.',
  banners: [
    ...['1080x1080', '1200x628'].map((size) => ({
      version_name: 'The morning shortcut',
      strategy: 'top_performer',
      version: firstBannerVersion,
      size,
      variation: 1,
      references_used: false,
      status: 'done',
      image_url: `gs://example-output/banners/${size}-1.png`,
      // Any image the app serves; the real service returns signed https URLs.
      playback_url: new URL('/images/workflows/storyboard.svg', location.origin).href,
      error: null,
      warnings: [],
    })),
    {
      version_name: 'Put it to the test',
      strategy: 'new_concept',
      version: thirdBannerVersion,
      size: '1080x1080',
      variation: 1,
      references_used: false,
      status: 'done',
      image_url: 'gs://example-output/banners/1080x1080-1.png',
      playback_url: new URL('/images/workflows/storyboard.svg', location.origin).href,
      error: null,
      warnings: ['No reference images loaded.'],
    },
    {
      version_name: 'Put it to the test',
      strategy: 'new_concept',
      version: thirdBannerVersion,
      size: '1200x628',
      variation: 1,
      references_used: false,
      status: 'failed',
      image_url: null,
      error: 'The image model timed out.',
      warnings: [],
    },
  ],
};
// Parsed twins of the raw fixtures above; the raw shapes stay for rawResult and malformed cases.
export const parsedIdeationBrief = ideationBriefSchema.parse(ideationBrief);
export const parsedStoryboardResult = storyboardResultSchema.parse(storyboardResult);
export const parsedVideoResult = videoResultSchema.parse(videoResult);
export const parsedBannerStoryboardResult =
  bannerStoryboardResultSchema.parse(bannerStoryboardResult);
export const parsedBannerResult = bannerResultSchema.parse(bannerResult);
type Scene = StoryboardVersion['scenes'][number];

export function storyboardScene(
  scene: Partial<Scene> & Pick<Scene, 'objective' | 'visual'>,
): Scene {
  return {
    shot_label: scene.objective,
    framing: 'Close-up',
    camera_style: 'Static',
    start_seconds: 0,
    end_seconds: 0,
    on_screen_text: '',
    voiceover: '',
    audio: '',
    cta: '',
    element_ids: [],
    ...scene,
  };
}
export function ideationRun(
  result: unknown,
  resultKind: string,
  actionType = 'generateIdeationBrief',
): WorkflowRun {
  return {
    runId: '00000000-0000-4000-8000-000000000001',
    actionType,
    status: 'completed',
    completedAt: null,
    result,
    resultSchemaVersion: 1,
    resultKind,
    actionName: 'Create ideation',
    outcomeTitle: 'Ideation brief',
    outcomeKind: 'brief',
    nextActions: [],
    itemCount: 1,
    errorMessage: null,
  };
}
